import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { PageTitle } from 'ui/PageTitle';
import { ESortOrder } from 'store/common/types';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar } from 'ui/NetworkStatusBar';
import * as AuthSelectors from 'store/modules/auth';
import { useHistory } from 'react-router';
import {
  ECrmDirectoryListSortableKey,
  IFilter,
  IFilterSearch,
} from 'services/CrmApi/types/DirectoryEntry';
import { useResponsive } from '../hooks/useResponsive';
import { getPageResultsText } from 'utils/string';
import { isNil } from 'lodash-es';
import { IUseCrmDirectoryEntriesParams, useCrmDirectoryEntries } from 'services/CrmApi/queries/useCrmDirectoryEntries';
import { usePagination } from 'hooks/usePagination';
import { useSorting } from 'hooks/useSorting';
import { CrmFilters } from './CrmFilters';
import { confirmedFiltersSelector, wholeFilterSelector } from 'store/modules/crm/selectors';
import * as CrmManagementActions from 'store/modules/crm/actions';
import { ETypeValueType } from 'services/CrmApi/types/types';
import { useSubscribeToAllNotificationsMutation, useUnsubscribeFromAllNotificationsMutation } from 'services/CrmApi/mutations/NotificationSubscriptions';
import { useNotifications } from 'hooks/useNotifications';
import { CrmDirectoryTable } from './CrmDirectoryTable';

export const CrmDirectory: React.FC = () => {
  const history = useHistory();
  const { isMobile, isTablet } = useResponsive();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const dispatch = useDispatch();
  const isSr = useSelector(AuthSelectors.isSr);

  const { sortBy, sortOrder, handleSort } = useSorting(ECrmDirectoryListSortableKey.CREATED_AT, ESortOrder.DESC);
  const {
    page: currentPage,
    setPage: setCurrentPage,
    perPage: itemsPerPage,
    setPerPage: setItemsPerPage,
    pageCount,
    setTotal,
  } = usePagination({ initialPage: 0, initialPerPage: 10, initialTotal: 0 });

  const transformFilter = (filter: IFilter): IFilterSearch => {
    const filterForSearch = {
      term: filter.term === '' ? undefined : filter.term,
      managerId: isNil(filter.managerId) ? undefined : filter.managerId,
      countryCodes: filter.countryCodes,
      companyTypeCodes: filter.typeCodes.filter(e => e.profileType === ETypeValueType.COMPANY).map(e => e.value),
      contactTypeCodes: filter.typeCodes.filter(e => e.profileType === ETypeValueType.CONTACT).map(e => e.value),
      leadStageCode: isNil(filter.leadStageCode) ? undefined : filter.leadStageCode,
      leadScoreGte: isNil(filter.leadScoreGte) ? undefined : filter.leadScoreGte === 0 ? null : filter.leadScoreGte,
      bookingActivityStatus: isNil(filter.bookingActivityStatus) ? undefined : filter.bookingActivityStatus,
    };
    delete (filterForSearch as any).typeCodes;
    return filterForSearch;
  };

  const filter = transformFilter(useSelector(wholeFilterSelector));
  const confirmedFilter = transformFilter(useSelector(confirmedFiltersSelector)); // filter that is effective after user clicks "search" button

  const params: IUseCrmDirectoryEntriesParams = {
    filter,
    pagination: {
      currentPage,
      itemsPerPage,
    },
    sorting: {
      sortBy,
      sortOrder,
    },
  };
  const {
    directoryEntries,
    directoryEntriesCount,
    crmDirectoryEntries,
  } = useCrmDirectoryEntries(params);
  const normalizedDirectoryEntriesCount = directoryEntriesCount ?? 0;

  useEffect(() => {
    if (directoryEntriesCount !== null) {
      setTotal(directoryEntriesCount);
    }
  }, [directoryEntriesCount, setTotal]);

  const subscribeToAllNotificationsMutation = useSubscribeToAllNotificationsMutation();
  const unsubscribeFromAllNotificationsMutation = useUnsubscribeFromAllNotificationsMutation();

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber - 1);
    },
    [setCurrentPage]
  );

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      setCurrentPage(0);
      setItemsPerPage(itemsPerPage);
    },
    [setCurrentPage, setItemsPerPage]
  );

  const handleSubscribeToAllNotifications = useCallback(async () => {
    dispatch(CrmManagementActions.setNotificationsSubscriptionPendingAction(true));
    try {
      await subscribeToAllNotificationsMutation.mutateAsync(confirmedFilter);

      const successMessage = 'Subscriptions created successfully.';
      showSuccessNotification(successMessage);
      dispatch(CrmManagementActions.closeSubscribeToAllNotificationsModalAction());
    } catch (error) {
      const errorMessage = 'Error creating subscriptions.';
      showErrorNotification(errorMessage);
    } finally {
      dispatch(CrmManagementActions.setNotificationsSubscriptionPendingAction(false));
    }
  }, [confirmedFilter, subscribeToAllNotificationsMutation, showSuccessNotification, showErrorNotification, dispatch]);

  const handleUnsubscribeFromAllNotifications = useCallback(async () => {
    dispatch(CrmManagementActions.setNotificationsSubscriptionPendingAction(true));
    try {
      await unsubscribeFromAllNotificationsMutation.mutateAsync(confirmedFilter);

      const successMessage = 'Subscriptions removed successfully.';
      showSuccessNotification(successMessage);
      dispatch(CrmManagementActions.closeUnsubscribeFromAllNotificationsModalAction());
    } catch (error) {
      const errorMessage = 'Error removing subscriptions.';
      showErrorNotification(errorMessage);
    } finally {
      dispatch(CrmManagementActions.setNotificationsSubscriptionPendingAction(false));
    }
  }, [confirmedFilter, unsubscribeFromAllNotificationsMutation, showSuccessNotification, showErrorNotification, dispatch]);

  if (crmDirectoryEntries.isPending || !directoryEntries) {
    return (
      <div className="mt-[20px]">
        <LoadingBar />
      </div>
    );
  }

  const paginationClassname = classNames({
    'flex flex-col gap-[15px]': isMobile || isTablet,
  });

  return (
    <div className="crm-directory mx-[20px] crm-desktop:mx-[80px]">
      <PageTitle
        primaryText="Directory"
        secondaryText={getPageResultsText(crmDirectoryEntries.isFetching, directoryEntriesCount)}
        secondaryCondition={true}
      />
      <CrmFilters
        directoryEntriesCount={normalizedDirectoryEntriesCount} // needed for showing the confirmation modals
        handlePageChange={handlePageChange}
        onSubscribeToAllNotifications={handleSubscribeToAllNotifications}
        onUnsubscribeFromAllNotifications={handleUnsubscribeFromAllNotifications}
      />
      <div className="mt-[20px]">
        <div className="directory-entry-list-content w-full">
          <CrmDirectoryTable
            directoryEntries={directoryEntries}
            isPending={crmDirectoryEntries.isPending}
            isFetching={crmDirectoryEntries.isFetching}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          {normalizedDirectoryEntriesCount > 0 && (
            <div className="mt-[30px]">
              <Pagination
                itemsPerPage={itemsPerPage}
                pageCount={pageCount}
                currentPage={currentPage + 1}
                className={paginationClassname}
                onPageSelect={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
